import { useEffect, useRef, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  addMonths,
  dayOfMonth,
  monthGrid,
  monthTitle,
  relativeDay,
  startOfMonth,
  weekdayShort,
} from '../lib/dates';
import { classNames } from '../lib/format';
import { useOnClickOutside } from '../lib/hooks';

export interface DayInfo {
  date: string;
  closed: boolean;
  available: boolean;
  bookable: boolean;
  note?: string | null;
}

/**
 * A strip of the next fortnight (the dates people book most) with a month
 * calendar behind "More dates". Closed and full days are marked, not hidden,
 * so guests understand why.
 */
export function DateStrip(props: {
  today: string;
  value: string;
  onChange: (date: string) => void;
  days: Map<string, DayInfo>;
  maxDate: string;
  loadMonth?: (month: string) => void;
}) {
  const stripRef = useRef<HTMLDivElement>(null);
  const strip = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(`${props.today}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + i);
    return d.toISOString().slice(0, 10);
  });
  const inStrip = strip.includes(props.value);

  useEffect(() => {
    const el = stripRef.current?.querySelector('[aria-pressed="true"]') as HTMLElement | null;
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }, [props.value]);

  return (
    <div className="date-strip-wrap">
      <div className="date-strip" ref={stripRef} role="group" aria-label="Choose a date">
        {strip.map((date) => {
          const info = props.days.get(date);
          const closed = info?.closed;
          const full = info && !info.closed && !info.available;
          return (
            <button
              key={date}
              type="button"
              className={classNames('date-cell', closed && 'is-closed', full && 'is-full')}
              aria-pressed={props.value === date}
              disabled={closed}
              onClick={() => props.onChange(date)}
              title={closed ? info?.note || 'Closed' : full ? 'Fully booked for this party size' : undefined}
            >
              <span className="date-dow">{date === props.today ? 'Today' : weekdayShort(date)}</span>
              <span className="date-num num">{dayOfMonth(date)}</span>
              <span className="date-note">{closed ? 'Closed' : full ? 'Full' : ' '}</span>
            </button>
          );
        })}
      </div>
      <CalendarPopover
        value={props.value}
        today={props.today}
        maxDate={props.maxDate}
        days={props.days}
        onChange={props.onChange}
        loadMonth={props.loadMonth}
        highlighted={!inStrip}
      />
    </div>
  );
}

function CalendarPopover(props: {
  value: string;
  today: string;
  maxDate: string;
  days: Map<string, DayInfo>;
  onChange: (d: string) => void;
  loadMonth?: (month: string) => void;
  highlighted: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(startOfMonth(props.value));
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setOpen(false), open);
  useEffect(() => {
    if (open) props.loadMonth?.(month);
  }, [open, month]); // eslint-disable-line react-hooks/exhaustive-deps

  const grid = monthGrid(month);
  const canPrev = month > startOfMonth(props.today);
  const canNext = addMonths(month, 1) <= props.maxDate;
  return (
    <div className="cal-wrap" ref={ref}>
      <button
        type="button"
        className={classNames('date-cell date-more', props.highlighted && 'is-picked')}
        onClick={() => {
          setMonth(startOfMonth(props.value));
          setOpen((o) => !o);
        }}
        aria-expanded={open}
      >
        <CalendarDays />
        <span className="date-note">{props.highlighted ? relativeDay(props.value, props.today) : 'More dates'}</span>
      </button>
      {open && (
        <div className="cal" role="dialog" aria-label="Pick a date">
          <div className="cal-head">
            <button type="button" className="btn btn-ghost btn-icon btn-sm" disabled={!canPrev} onClick={() => setMonth(addMonths(month, -1))} aria-label="Previous month">
              <ChevronLeft />
            </button>
            <strong>{monthTitle(month)}</strong>
            <button type="button" className="btn btn-ghost btn-icon btn-sm" disabled={!canNext} onClick={() => setMonth(addMonths(month, 1))} aria-label="Next month">
              <ChevronRight />
            </button>
          </div>
          <div className="cal-grid">
            {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d) => (
              <span key={d} className="cal-dow">
                {d}
              </span>
            ))}
            {grid.map((date) => {
              const info = props.days.get(date);
              const outside = date.slice(0, 7) !== month.slice(0, 7);
              const disabled = date < props.today || date > props.maxDate || info?.closed;
              return (
                <button
                  key={date}
                  type="button"
                  className={classNames('cal-day', outside && 'is-outside', info && !info.closed && !info.available && 'is-full', date === props.today && 'is-today')}
                  aria-pressed={props.value === date}
                  disabled={disabled}
                  onClick={() => {
                    props.onChange(date);
                    setOpen(false);
                  }}
                >
                  {dayOfMonth(date)}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
